import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { LTText } from '@lambdatestincprivate/lt-components';
import { ThemeProvider, useTheme } from '@primer/react';
import SummaryCard from './SummaryCard';
import InsightChart from './InsightChart';
import { WIDGET } from './buildInsightsQueries';

// The single shared, product-agnostic Insights view. Reads no Redux and makes no direct HTTP call —
// the host app injects:
//   build       : normalized { id, name, buildType, status, testCount, createdAt }
//   widgets      : ordered list of widget keys (APP_WIDGETS / WEB_WIDGETS)
//   summaryData  : the testSummary payload (or null while loading) — the host fetches it via its own
//                  redux/saga flow and feeds it in, so this component stays HTTP-agnostic & pure.

// Fits the area below the tab strip without scrolling (rows are sized in px to the measured height),
// with an even 16px border on all four sides (no overflow => padding renders on every side).
const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  height: 100%;
  overflow: hidden;
`;

// Height is set inline from the measured/computed row height (see below) so charts fill the space.
const Row = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const Col = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
`;

// Default chart type per widget; widgets not listed render with their config's native type.
const CHART_TYPE = {
  [WIDGET.BUILDS_SUMMARY_INSIGHTS]: 'donut',
  [WIDGET.DEVICES]: 'donut',
  [WIDGET.BROWSER]: 'donut',
  [WIDGET.OS]: 'donut',
  [WIDGET.BUILD_HISTORY]: 'bar',
  [WIDGET.FAILURE_CATEGORIES]: 'bar',
};

const BAR_META = { maxColumnWidth: 12, radius: [4, 4, 0, 0], stackedSpacing: true, isDateChange: true };

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Primer's font-size scale (index -> px). The atx-sdk billboard uses numeric scale indices (e.g.
// fontSize: 4 -> 24px). Some host themes ship a truncated `fontSizes` (only 0–2), so indices 3+
// resolve to literal px (4 -> 4px) and the metric values render tiny. We restore a complete scale —
// once here, scoped to all widgets — keeping any host overrides for 0–2.
const PRIMER_FONT_SIZES = ['12px', '14px', '16px', '20px', '24px', '32px', '40px', '48px', '56px'];

const BuildInsights = ({ build, widgets = [], summaryData }) => {
  const [isNarrow, setIsNarrow] = useState(false);
  // Responsive: 2 widgets per row normally, stacked to 1 per row when the panel is narrow.
  const rows = useMemo(() => chunk(widgets, isNarrow ? 1 : 2), [widgets, isNarrow]);
  const { theme } = useTheme();
  // Patch the font-size scale ONCE for the whole widget tree (not per chart). Preserve host overrides
  // for 0–2; fill the rest from Primer defaults.
  const themeWithFontSizes = useMemo(
    () => ({ ...theme, fontSizes: PRIMER_FONT_SIZES.map((v, i) => theme?.fontSizes?.[i] ?? v) }),
    [theme]
  );

  // Dynamic widget height: the SDK charts render at a fixed customHeight (no container autofit), so to
  // make the panel fit one page we measure the available height (the Grid minus its padding) and the
  // summary card, then divide the rest among the rows — and hand each chart an explicit px height.
  // Setting row height in px (not flex) keeps it deterministic and avoids the flex-shrink ambiguity.
  const gridRef = useRef(null);
  const summaryRef = useRef(null);
  const [rowH, setRowH] = useState(300);
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === 'undefined') return undefined;
    const PAD = 16;
    const GAP = 16;
    const BREAKPOINT = 768; // below this CONTAINER width, stack to a single column
    const recompute = () => {
      const narrow = grid.clientWidth > 0 && grid.clientWidth < BREAKPOINT;
      setIsNarrow(narrow);
      const cols = narrow ? 1 : 2;
      const numRows = Math.ceil((widgets.length || 1) / cols);
      if (narrow) {
        // Stacked: too many rows to fit one page at a usable size, so use a fixed height and let it
        // scroll (the fit-to-one-page behavior is for the wide 2-up view).
        setRowH(320);
        return;
      }
      const inner = grid.clientHeight - PAD * 2; // content area (clientHeight includes padding)
      const summaryH = summaryRef.current ? summaryRef.current.offsetHeight : 0;
      const gaps = GAP * numRows; // summary->row1, row1->row2, ...
      const h = Math.floor((inner - summaryH - gaps) / numRows);
      setRowH(Math.max(h, 160)); // floor so charts stay legible on very short screens
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(grid);
    if (summaryRef.current) ro.observe(summaryRef.current);
    return () => ro.disconnect();
  }, [build?.id, widgets.length, summaryData]);

  // No build context -> nothing to scope insights to.
  if (!build?.id) {
    return (
      <EmptyState>
        <LTText text="Insights are unavailable for this build." color="#636C76" />
      </EmptyState>
    );
  }

  return (
    <ThemeProvider theme={themeWithFontSizes}>
      <Grid ref={gridRef} style={{ overflow: isNarrow ? 'auto' : 'hidden' }}>
        <div ref={summaryRef}>
          <SummaryCard successRateData={summaryData} stacked={isNarrow} />
        </div>
        {rows.map((pair, idx) => (
          <Row key={pair.join('-') || idx} style={{ height: rowH }}>
            {pair.map((widgetType) => {
              const chartType = CHART_TYPE[widgetType];
              return (
                <Col key={widgetType}>
                  <InsightChart
                    widgetType={widgetType}
                    build={build}
                    chartType={chartType}
                    metadata={chartType === 'bar' ? BAR_META : undefined}
                    customHeight={Math.max(rowH - 2, 140)}
                    height={Math.max(rowH - 72, 90)}
                  />
                </Col>
              );
            })}
          </Row>
        ))}
      </Grid>
    </ThemeProvider>
  );
};

export default BuildInsights;
